import React from "react";
import {Button, CircularProgress, withStyles} from "@material-ui/core";
import History from "../../../History";
import PlanFormDetails from "./PlanFormDetails";
import PlanFormConfiguration from "./PlanFormConfiguration";
import moment from "moment";
import * as Yup from "yup";
import {Formik} from "formik";
import PlanFormPricelist from "./PlanFormPricelist";
import {hasPermission} from "../../sc-utilities";

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    submit: {
        width: '120px',
        marginTop: '10px',
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    cancel: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    formControl: {
        width: '100%'
    },
    datePicker: {
        width: '100%'
    },
    editSubmit: {
        width: '100%',
        marginTop: '10px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
});

const PlanForm = (props) => {
    const { translations, classes, submitHandler, activeStep, setActiveStep, settingsAndValues, permissions, isEdit, billingPlan, setModalState } = props;

    const cancelHandler = () => {
        setActiveStep(0);
        History.goBack();
    }
    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };
    const handleNext = () => {
        setActiveStep(activeStep + 1);
    };

    const statuses = [{ value: 'active', displayValue: translations["active"] || 'Active' }, { value: 'inactive', displayValue: translations["inactive"] || 'Inactive' }];

    const priceListValidation = Yup.array().of(
        Yup.object().shape({
            from: Yup.number().integer().required(),
            to: Yup.number().integer().nullable(),
            price: Yup.number().min(0.1).required()
        })
    ).test('Test', translations['from_must_be_greater_than_previous'] || 'From must be greater than previous from! ', (values) => {
        return !values.some((value, index) => values[index-1]? (value.from <= values[index-1].from && (value.to===null || value.from <= value.to)) : false);
    });

    const initialValues = {
        Name: '',
        Description: '',
        Status: 'active',
        ValidFrom: moment(),
        ValidTo: null,
        MinimalUsage: 100,
        IncludedUsage: 100000,
        AllowedOveruse: '',
        MaxAllowedUsers: 1000,
        MaxRemoteDiskNumber: 3,
        ErasureRedemptionPeriod: 30,
        DiagnoseRedemptionPeriod: 30,
        CheckRedemptionPeriod: 30,
        VerificationRedemptionPeriod: 30,
        MonthlyPrice: 0,
        Mobile: [],
        Work: [],
        Diagnose: [],
        Remote: [],
        PrepaidAllowed: false,
        PostpaidAllowed: false
    };

    const formSettings = {
        Validation: activeStep < 2 ? {
            Name: Yup.string().required('Required'),
            Description: Yup.string().required('Required'),
            Status: Yup.string().required('Required'),
            ValidFrom: Yup.date()
                .notRequired()
                .nullable(true),
            ValidTo: Yup.date()
                .notRequired()
                .nullable(true).min(Yup.ref('ValidFrom')),
            AllowedOveruse: Yup.number().notRequired(),
            MinimalUsage: Yup.number().integer().required('Required').min(0),
            IncludedUsage: Yup.number().integer().required('Required').min(0),
            MaxAllowedUsers: Yup.number().integer().required('Required').min(0),
            MaxRemoteDiskNumber: Yup.number().integer().required().min(0),
            ErasureRedemptionPeriod: Yup.number().integer().required().min(0),
            DiagnoseRedemptionPeriod: Yup.number().integer().required().min(0),
            CheckRedemptionPeriod: Yup.number().integer().required().min(0),
            VerificationRedemptionPeriod: Yup.number().integer().required().min(0),
            MonthlyPrice: Yup.number().required('Required').min(0),
            PrepaidAllowed: Yup.boolean().notRequired(),
            PostpaidAllowed: Yup.boolean().notRequired(),

        } : {
            'Mobile Erasure': priceListValidation,
            'Mobile Erasure Lite': priceListValidation,
            'Mobile Erasure Verification': priceListValidation,
            'Mobile Diagnose': priceListValidation,
            'Mobile Check': priceListValidation,
            'Work Erasure': priceListValidation,
            'Work Erasure Verification': priceListValidation,
            'Work Diagnose': priceListValidation,
            'Work Check': priceListValidation,
            'Watch Erasure': priceListValidation,
            'Watch Diagnose': priceListValidation,
            'Watch Check': priceListValidation,
            'Headphone Diagnose': priceListValidation,
            'Headphone Check': priceListValidation,
            Remote: priceListValidation,
        },


        ButtonSubmit: hasPermission(permissions, 'plan_create') ? {
            Title: translations['save'] || 'SAVE',
            Color: 'primary'
        }: null,
        ButtonCancel: {
            Title: translations['cancel'] || 'Cancel',
            handleCancel: cancelHandler
        },
        ButtonBack: {
            Title: translations['back'] || 'BACK',
            Color: 'primary',
            handleBack: handleBack
        },
        ButtonNext: {
            Title: translations['next'] || 'NEXT',
            Color: 'primary',
            handleNext: handleNext
        }
    };

    const formValues = settingsAndValues?.initialValues ?? initialValues;
    const settings = settingsAndValues?.formSettings?.Validation ?? formSettings.Validation;

    const handleCreate = async (values, setSubmitting) => {
        setSubmitting(true);
        await submitHandler(values);
        setSubmitting(false);
    }

    const handleEdit = (values) => {
        setModalState((previousState) => { return {open: true, values: {...previousState.values, ...values}}})
    }

    return (
        <Formik
            initialValues={formValues}
            onSubmit={(values, { setSubmitting }) => {
                isEdit? handleEdit(values) : handleCreate(values, setSubmitting);
            }}
            validationSchema={Yup.object().shape(settings)}
        >
            {formikProps => {
                const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue } = formikProps;

                const propsToPass = {
                    values,
                    translations,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    statuses,
                    isValid,
                    isEdit,
                    billingPlan,
                    permissions
                }

                return (
                    <div className={classes.root}>
                        <form onSubmit={handleSubmit}>
                            {activeStep === 0 && <PlanFormDetails {...propsToPass} />}
                            {activeStep === 1 && <PlanFormConfiguration {...propsToPass} />}
                            {activeStep === 2 &&
                            <>
                                <PlanFormPricelist {...propsToPass} priceListName={"Mobile Erasure"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Mobile Erasure Lite"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Mobile Erasure Verification"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Mobile Diagnose"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Mobile Check"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Work Erasure"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Work Erasure Verification"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Work Diagnose"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Work Check"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Watch Erasure"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Watch Diagnose"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Watch Check"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Headphone Diagnose"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Headphone Check"} />
                                <PlanFormPricelist {...propsToPass} priceListName={"Remote"} />
                            </>}

                            {!settingsAndValues?.initialValues && <div style={{ textAlign: 'right' }}>
                                {formSettings.ButtonCancel && (
                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        color={formSettings.ButtonCancel.Color}
                                        onClick={formSettings.ButtonCancel.handleCancel}
                                    >
                                        {formSettings.ButtonCancel.Title}
                                    </Button>
                                )}
                                {formSettings.ButtonBack && (
                                    <Button
                                        style={{ width: '120px', marginTop: 10, marginRight: 10, display: activeStep === 0 ? 'none' : null }}
                                        variant='contained'
                                        disabled={activeStep === 0}
                                        color={formSettings.ButtonBack.Color}
                                        onClick={formSettings.ButtonBack.handleBack}
                                    >
                                        {formSettings.ButtonBack.Title}
                                    </Button>
                                )}
                                {formSettings.ButtonNext && (
                                    <Button
                                        style={{ width: '120px', marginTop: 10, marginRight: 10, display: activeStep > 1 ? 'none' : null }}
                                        variant='contained'
                                        disabled={!(isValid && dirty) || isSubmitting || (!values.PostpaidAllowed && !values.PrepaidAllowed)}
                                        color={formSettings.ButtonNext.Color}
                                        onClick={formSettings.ButtonNext.handleNext}
                                    >
                                        {formSettings.ButtonNext.Title}
                                    </Button>
                                )}
                                {formSettings.ButtonSubmit && activeStep===2 ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? <CircularProgress size={24}/> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>}

                            {isEdit && settingsAndValues?.formSettings?.ButtonSubmit ? (
                                hasPermission(permissions, 'plan_update') && <Button
                                        className={classes.editSubmit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        disabled={!(isValid && dirty)}
                                    >
                                        {formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                        </form>
                    </div>
                );
            }}
        </Formik>
    )
}

export default (withStyles(styles)(PlanForm));